import React, { useEffect, useState } from "react";

import { collection, getDocs } from "firebase/firestore";

import {
  FaImages,
  FaSearch,
  FaMapMarkerAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaVideo,
  // FaPlay,
} from "react-icons/fa";

import { db } from "../../firebase/firebase";

import "./Gallery.css";

const Gallery = () => {
  const [sites, setSites] = useState([]);

  const [filteredSites, setFilteredSites] = useState([]);

  const [search, setSearch] = useState("");

  const [stateFilter, setStateFilter] = useState("All");

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [selectedSite, setSelectedSite] = useState(null);

  const [selectedMedia, setSelectedMedia] = useState(null);

  // =====================================================
  // LOAD GALLERY
  // =====================================================

  const loadGallery = async () => {
    try {
      setLoading(true);
      setError("");

      const snapshot = await getDocs(collection(db, "gallerySites"));

      const gallerySites = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setSites(gallerySites);
      setFilteredSites(gallerySites);
    } catch (err) {
      console.error("Gallery loading error:", err);

      setError(err.message || "Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGallery();
  }, []);

  // =====================================================
  // FILTER
  // =====================================================

  useEffect(() => {
    const text = search.toLowerCase().trim();

    const filtered = sites.filter((site) => {
      const matchesSearch =
        (site.siteName || "").toLowerCase().includes(text) ||
        (site.location || "").toLowerCase().includes(text) ||
        (site.state || "").toLowerCase().includes(text);

      const matchesState = stateFilter === "All" || site.state === stateFilter;

      return matchesSearch && matchesState;
    });

    setFilteredSites(filtered);
  }, [search, stateFilter, sites]);

  // =====================================================
  // STATES
  // =====================================================

  const states = [...new Set(sites.map((site) => site.state).filter(Boolean))];

  // =====================================================
  // OPEN SITE
  // =====================================================

  const openSite = (site) => {
    setSelectedSite(site);
    setSelectedMedia(null);
  };

  // =====================================================
  // CLOSE SITE
  // =====================================================

  const closeSite = () => {
    setSelectedSite(null);
    setSelectedMedia(null);
  };

  // =====================================================
  // OPEN MEDIA
  // =====================================================

  const openMedia = (media) => {
    setSelectedMedia(media);
  };

  // =====================================================
  // CLOSE MEDIA
  // =====================================================

  const closeMedia = () => {
    setSelectedMedia(null);
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <section className="public-gallery-page">
        <div className="public-gallery-loading">
          <FaImages />

          <h2>Loading Gallery...</h2>

          <p>Loading our latest sports projects.</p>
        </div>
      </section>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <section className="public-gallery-page">
        <div className="public-gallery-empty">
          <FaImages />

          <h2>Gallery Unavailable</h2>

          <p>{error}</p>

          <button onClick={loadGallery}>Try Again</button>
        </div>
      </section>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <section className="public-gallery-page">
      {/* HERO */}

      <div className="public-gallery-hero">
        <div className="public-gallery-hero-content">
          <span className="public-gallery-tag">OUR WORK</span>

          <h1>
            Project
            <span> Gallery</span>
          </h1>

          <p>
            Explore our completed sports construction sites, premium surfaces
            and professionally built sporting facilities.
          </p>
        </div>
      </div>

      {/* CONTENT */}

      <div className="public-gallery-container">
        {/* TOOLBAR */}

        <div className="public-gallery-toolbar">
          <div className="public-gallery-search">
            <FaSearch />

            <input
              type="text"
              placeholder="Search site name or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="All">All States</option>

            {states.map((item) => (
              <option value={item} key={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        {/* COUNT */}

        <div className="public-gallery-count">
          Showing <strong>{filteredSites.length}</strong> sites
        </div>

        {/* SITES */}

        {filteredSites.length > 0 ? (
          <div className="public-gallery-sites">
            {filteredSites.map((site) => (
              <article
                className="public-gallery-site-card"
                key={site.id}
                onClick={() => openSite(site)}
              >
                <div className="public-gallery-site-image">
                  {site.images?.length > 0 ? (
                    <img
                      src={site.images[0].url}
                      alt={site.siteName}
                      loading="lazy"
                    />
                  ) : (
                    <div className="public-gallery-no-image">
                      <FaImages />
                    </div>
                  )}

                  <div className="public-gallery-site-overlay">
                    <span>VIEW SITE</span>
                  </div>

                  <div className="public-gallery-media-badges">
                    <span>
                      <FaImages />
                      {site.images?.length || 0}
                    </span>

                    <span>
                      <FaVideo />
                      {site.videos?.length || 0}
                    </span>
                  </div>
                </div>

                <div className="public-gallery-site-content">
                  <h2>{site.siteName}</h2>

                  <p>
                    <FaMapMarkerAlt />

                    {site.location}

                    {site.state ? `, ${site.state}` : ""}
                  </p>

                  {site.description && (
                    <div className="public-gallery-site-description">
                      {site.description}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openSite(site);
                    }}
                  >
                    View Photos & Videos
                    <FaChevronRight />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="public-gallery-empty">
            <FaImages />

            <h2>No Sites Found</h2>

            <p>No gallery sites match your search.</p>
          </div>
        )}
      </div>

      {/* =================================================
          SITE MODAL
      ================================================= */}

      {selectedSite && (
        <div className="public-gallery-site-modal" onClick={closeSite}>
          <div
            className="public-gallery-site-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="public-gallery-site-modal-header">
              <div>
                <h2>{selectedSite.siteName}</h2>

                <p>
                  <FaMapMarkerAlt />

                  {selectedSite.location}

                  {selectedSite.state ? `, ${selectedSite.state}` : ""}
                </p>
              </div>

              <button onClick={closeSite}>
                <FaTimes />
              </button>
            </div>

            {/* DESCRIPTION */}

            {selectedSite.description && (
              <div className="public-gallery-site-modal-description">
                {selectedSite.description}
              </div>
            )}

            {/* IMAGES */}

            {selectedSite.images?.length > 0 && (
              <section className="public-gallery-media-section">
                <h3>
                  <FaImages />
                  Photos
                </h3>

                <div className="public-gallery-photo-grid">
                  {selectedSite.images.map((image, index) => (
                    <div
                      className="public-gallery-photo"
                      key={image.publicId || image.url || index}
                      onClick={() =>
                        openMedia({
                          ...image,
                          type: "image",
                        })
                      }
                    >
                      <img src={image.url} alt={selectedSite.siteName} />

                      <div>View Photo</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* VIDEOS */}

            {selectedSite.videos?.length > 0 && (
              <section className="public-gallery-media-section">
                <h3>
                  <FaVideo />
                  Videos
                </h3>

                <div className="public-gallery-video-grid">
                  {selectedSite.videos.map((video, index) => (
                    <div
                      className="public-gallery-video"
                      key={video.publicId || video.url || index}
                    >
                      <video src={video.url} controls preload="metadata" />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {/* =================================================
          IMAGE LIGHTBOX
      ================================================= */}

      {selectedMedia && selectedMedia.type === "image" && (
        <div className="public-gallery-lightbox" onClick={closeMedia}>
          <button
            className="public-gallery-lightbox-close"
            onClick={closeMedia}
          >
            <FaTimes />
          </button>

          <button className="public-gallery-lightbox-prev" onClick={closeMedia}>
            <FaChevronLeft />
          </button>

          <img
            src={selectedMedia.url}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />

          <button className="public-gallery-lightbox-next" onClick={closeMedia}>
            <FaChevronRight />
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
